import { useEffect } from "react";
import { Link } from "react-router-dom";
import { HandyFaq } from "@/components/HandyFaq";
import { PriceCalculator } from "@/components/preise/PriceCalculator";
import { PriceList } from "@/components/preise/PriceList";
import {
    Smartphone, Battery, Zap, Camera, Cpu, Clock,
    MessageCircle, Phone, ArrowUpRight, CheckCircle2
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1706300896410-73677aacc871?crop=entropy&cs=srgb&fm=jpg&w=1000&q=85";

const repairs = [
    {
        icon: Smartphone,
        title: "Display-Reparatur",
        text: "Gesprungenes oder defektes Display? Wir tauschen Displays, Touchscreens und Schutzgläser für iPhone, Samsung, Huawei, Xiaomi und viele weitere Modelle – meist innerhalb von 60–120 Minuten.",
    },
    {
        icon: Battery,
        title: "Akku-Tausch",
        text: "Hält Ihr Akku keinen Tag mehr durch? Wir ersetzen schwache Akkus durch neue, hochwertige Zellen. Längere Laufzeit, bessere Performance – fast immer sofort erledigt.",
    },
    {
        icon: Zap,
        title: "Ladebuchse & Anschlüsse",
        text: "Lightning-, USB-C- oder Micro-USB-Buchse defekt oder verschmutzt? Wir reinigen, reparieren oder tauschen den Ladeport, damit Ihr Handy wieder zuverlässig lädt.",
    },
    {
        icon: Camera,
        title: "Kamera & Lautsprecher",
        text: "Verschmierte Fotos, kratziger Ton oder stummes Mikrofon? Wir tauschen Front- und Rückkameras, Lautsprecher, Hörmuschel und Mikrofon fachgerecht aus.",
    },
    {
        icon: Cpu,
        title: "Software & Daten",
        text: "Handy startet nicht mehr, hängt im Logo oder zeigt Fehlermeldungen? Wir übernehmen Software-Reset, Firmware-Updates sowie – wenn möglich – Datenrettung.",
    },
];

const brands = ["iPhone", "Samsung", "Huawei", "Xiaomi", "Google Pixel", "OnePlus", "Oppo", "Sony"];

export default function Handyreparatur() {
    useEffect(() => {
        const prev = document.title;
        document.title = "Handyreparatur Regensburg (DEZ) – iPhone & Samsung Reparatur | Mein Schuster";
        return () => { document.title = prev; };
    }, []);

    return (
        <div data-testid="page-handyreparatur">
            {/* HERO */}
            <section className="relative border-b border-line overflow-hidden" data-testid="handy-hero">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-16 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <p className="font-body text-xs tracking-[0.3em] uppercase font-semibold text-brand mb-6">
                            NEU IM DEZ
                        </p>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-[80px] tracking-tighter leading-[0.95] font-medium text-ink">
                            Handy-<br/>
                            <span className="text-brand italic">reparatur</span>.
                            <span className="sr-only"> in Regensburg im DEZ</span>
                        </h1>
                        <p className="mt-7 font-body text-lg md:text-xl font-light text-inkSoft max-w-2xl leading-snug">
                            Handy Reparatur in Regensburg: Display tauschen, Akku tauschen, Ladebuchse, Kamera &amp; mehr – schnell, fair und direkt im Donau-Einkaufszentrum (DEZ).
                        </p>

                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
                            <Feature icon={Clock} label="Meist 60–120 Min" />
                            <Feature icon={CheckCircle2} label="Alle Marken" />
                        </div>

                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <a
                                href="tel:+499414610027"
                                data-testid="handy-cta-call"
                                className="inline-flex items-center gap-3 bg-brand text-paper px-7 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-brandDark transition-colors"
                            >
                                <Phone size={14} /> Jetzt anrufen
                            </a>
                            <a
                                href="https://wa.me/4917620366268?text=Hallo%2C%20ich%20h%C3%A4tte%20gerne%20eine%20Handyreparatur%3A"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="handy-cta-whatsapp"
                                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-7 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#1DA851] transition-colors"
                            >
                                <MessageCircle size={14} /> Kostenvoranschlag per WhatsApp
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
                            <img src={HERO_IMG} alt="Handyreparatur im DEZ Regensburg" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 ring-1 ring-ink/10" />
                        </div>
                    </div>
                </div>
            </section>

            {/* BRAND BAR */}
            <section className="bg-ink text-paper py-8 border-y border-ink/50" data-testid="handy-brands">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
                    <span className="font-body text-[10px] tracking-[0.3em] uppercase text-paper/50 shrink-0">Wir reparieren</span>
                    {brands.map((b) => (
                        <span key={b} className="font-display text-xl md:text-2xl tracking-tight text-paper/90">
                            {b}
                        </span>
                    ))}
                </div>
            </section>

            {/* REPAIR SERVICES */}
            <section className="py-20 md:py-28" data-testid="handy-services">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="max-w-2xl mb-14">
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1] font-medium text-ink">
                            Unsere <span className="italic text-brand">Reparaturleistungen</span>.
                        </h2>
                        <p className="mt-5 font-body text-base md:text-lg font-light text-inkSoft">
                            Fachgerechte Reparatur mit Original- oder OEM-Ersatzteilen. Transparente Preise, meistens sofort.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {repairs.map((r) => (
                            <article
                                key={r.title}
                                data-testid={`handy-service-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="group bg-white border border-ink/10 p-8 hover:border-brand/60 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                            >
                                <div className="w-12 h-12 flex items-center justify-center bg-brand/10 text-brand mb-6 group-hover:bg-brand group-hover:text-paper transition-colors">
                                    <r.icon size={22} strokeWidth={1.5} />
                                </div>
                                <h3 className="font-display text-2xl md:text-3xl tracking-tight leading-tight text-ink mb-3">
                                    {r.title}
                                </h3>
                                <p className="font-body text-sm font-light text-inkSoft leading-relaxed">
                                    {r.text}
                                </p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="py-20 md:py-24 bg-paper border-y border-line" data-testid="handy-process">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1] text-ink mb-12 max-w-xl">
                        So läuft Ihre Reparatur ab.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <Step n="01" title="Vorbeikommen" text="Einfach ins DEZ, Ebene 2 – ohne Termin." />
                        <Step n="02" title="Diagnose" text="Wir prüfen Ihr Gerät direkt vor Ort und nennen den Festpreis." />
                        <Step n="03" title="Reparatur" text="Die meisten Reparaturen dauern 60–120 Minuten." />
                        <Step n="04" title="Testen & Mitnehmen" text="Wir testen gemeinsam mit Ihnen – und los geht's." />
                    </div>
                </div>
            </section>

            <PriceCalculator />
            <PriceList />

            <HandyFaq />

            {/* CTA */}
            <section className="py-20">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="bg-ink text-paper p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                        <div className="max-w-xl">
                            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
                                Unverbindlichen Kostenvoranschlag erhalten.
                            </h2>
                            <p className="mt-4 font-body text-sm md:text-base font-light text-paper/70">
                                Schicken Sie uns einfach ein Foto Ihres Geräts per WhatsApp – wir melden uns mit einem Festpreis.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-4 shrink-0">
                            <a
                                href="https://wa.me/4917620366268?text=Hallo%2C%20ich%20brauche%20einen%20Kostenvoranschlag%20f%C3%BCr%20eine%20Handyreparatur."
                                target="_blank" rel="noopener noreferrer"
                                data-testid="handy-footer-whatsapp"
                                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#1DA851] transition-colors"
                            >
                                <MessageCircle size={14} /> WhatsApp
                            </a>
                            <Link
                                to="/kontakt"
                                className="inline-flex items-center gap-3 bg-brand text-paper px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-brandDark transition-colors"
                            >
                                Kontakt <ArrowUpRight size={16} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Feature({ icon: Icon, label }) {
    return (
        <div className="flex items-center gap-2.5 text-ink">
            <Icon size={18} className="text-brand shrink-0" strokeWidth={1.75} />
            <span className="font-body text-sm font-medium">{label}</span>
        </div>
    );
}

function Step({ n, title, text }) {
    return (
        <div className="border-t border-ink/15 pt-6">
            <p className="font-display text-5xl text-brand/60 italic mb-4">{n}</p>
            <h3 className="font-display text-xl tracking-tight text-ink mb-2">{title}</h3>
            <p className="font-body text-sm font-light text-inkSoft leading-relaxed">{text}</p>
        </div>
    );
}
